import React, { useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";

import AppState from "../../store/AppState";
import { ViewContext } from "../../context/view-context";
import {
  selectAllProductsFetched,
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectProductAnalyticsDataById,
} from "../../store/product/ProductSelectors";
import {
  selectHeatMapMetric,
  selectHeatMapBatchPages,
  selectHeatMapIsFetching,
  selectHeatMapFreshProductCodes,
  selectHeatMapResolvedDateRange,
  selectHeatMapColorScales,
} from "../../store/heatmap/HeatMapSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import {
  setSelectedItems,
  setPendingScrollTarget,
} from "../../store/product-list/ProductListActions";
import { ProductSequenceData } from "../../store/product-list/ProductListTypes";
import { formatHeatMapMetricValue } from "../../utils/HeatMapUtils";
import { isHeatMapMetricDateReliant } from "./HeatMapMetricSelect";
import {
  StyledPlanogramPane,
  StyledPlanogramBody,
  StyledPlanogramGridWrapper,
  StyledPlanogramCell,
  StyledColorScale,
  StyledColorScaleBand,
  StyledColorScaleValue,
  StyledPageColumns,
  StyledPageColumn,
  StyledPageColumnLabel,
  StyledPageGrid,
  heatMapModalStyles,
} from "./HeatMapModalStyles";

/* Cell-size constants. */
const CELL_MARGIN = 2;
const SIDE_LENGTH_LG = 40;
const SIDE_LENGTH_MED = 20;
const SIDE_LENGTH_SML = 10;
const BORDER_RADIUS_LG = 5;
const BORDER_RADIUS_MED = 3;
const BORDER_RADIUS_SML = 0;

/**
 * Right pane — renders products from the active heat map batch (≤240
 * across up to 10 catalog pages) as a wrapping row of `P#N` page-columns.
 *
 * Each page-column has a header (`P#1`, `P#2`, …) and a sub-grid of
 * cells laid out using the main grid's column count (ViewContext).
 *
 * Layout wraps to a second row when 10 page-columns don't fit in the
 * modal width`.heat-map-page-container { float: left }`.
 *
 * Cell size is adaptive (40 / 20 / 10 px) based on total page count.
 */
const HEAT_MAP_MODAL_ID = "heatMapModal";

const HeatMapPlanogram: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const styles = heatMapModalStyles();
  const view = useContext(ViewContext);
  const columns = view.styles.column;

  const metric = useSelector(selectHeatMapMetric);
  const scales = useSelector(selectHeatMapColorScales);
  const batchPages = useSelector(selectHeatMapBatchPages);
  const isFetching = useSelector(selectHeatMapIsFetching);
  const freshProductCodes = useSelector(selectHeatMapFreshProductCodes);
  const productsById = useSelector(selectAllProductsFetched);
  const resolvedDateRange = useSelector(selectHeatMapResolvedDateRange);
  const cachedStartDate = useSelector(selectAnalyticsStartDate);
  const cachedEndDate = useSelector(selectAnalyticsEndDate);

  const isDateReliant = isHeatMapMetricDateReliant(metric);
  // The store's analytics cache range matches what the heat map is
  // currently displaying — when it does, cached entries (e.g. populated
  // by the Analytics overlay before the heat map opened) are valid and
  // should be treated as fresh even though no heat-map-side SUCCESS ever
  // fired for them.
  const cacheIsFreshForRange =
    resolvedDateRange.startDate === cachedStartDate &&
    resolvedDateRange.endDate === cachedEndDate;

  // Pull the metric value for every product across all pages of the batch.
  // Per-product freshness: for date-reliant metrics, a value is only
  // considered "fresh" if the product's analyticsId is in
  // `freshProductCodes` (i.e. its chunk has settled for the current range).
  // Non-date-reliant metrics (Inventory, Price, Stock Value, SKU Coverage)
  // come from product catalog data and are always considered fresh.
  const { valuesByProductId, freshByProductId } = useSelector(
    (state: AppState) => {
      const valueMap = new Map<string, number | undefined>();
      const freshMap = new Map<string, boolean>();
      batchPages.pages.forEach((page) => {
        page.productIds.forEach((productId) => {
          const analytics = selectProductAnalyticsDataById(state, productId);
          const found = analytics?.find((a) => a.name === metric);
          const raw = found?.value;
          const numeric =
            typeof raw === "number"
              ? raw
              : typeof raw === "string"
                ? parseFloat(raw)
                : undefined;
          valueMap.set(
            productId,
            Number.isFinite(numeric) ? (numeric as number) : undefined,
          );
          if (!isDateReliant) {
            freshMap.set(productId, true);
          } else {
            const analyticsId = productsById[productId]?.analyticsId;
            // Fresh if either: this modal session's SUCCESS marked it
            // fresh, OR the global analytics cache holds an entry for
            // it under the same range (e.g. pre-loaded by the
            // Analytics overlay).
            const heatMapMarkedFresh = Boolean(
              analyticsId && freshProductCodes[analyticsId],
            );
            const cacheMarkedFresh = Boolean(
              cacheIsFreshForRange &&
                analyticsId &&
                state.products.analytics.productAnalytics[analyticsId],
            );
            freshMap.set(productId, heatMapMarkedFresh || cacheMarkedFresh);
          }
        });
      });
      return { valuesByProductId: valueMap, freshByProductId: freshMap };
    },
  );

  // Adaptive cell sizing: > 10 → SML, > 3 → MED, else LG`
  // based on number of pages. With ≤10 pages we'll usually land on MED.
  const { sideLength, borderRadius } = useMemo(() => {
    const numOfPages = batchPages.pages.length;
    if (numOfPages > 10) {
      return { sideLength: SIDE_LENGTH_SML, borderRadius: BORDER_RADIUS_SML };
    } else if (numOfPages > 3) {
      return { sideLength: SIDE_LENGTH_MED, borderRadius: BORDER_RADIUS_MED };
    }
    return { sideLength: SIDE_LENGTH_LG, borderRadius: BORDER_RADIUS_LG };
  }, [batchPages.pages.length]);

  // Assign each product its color band. The color scale itself is
  // computed by `selectHeatMapColorScales` (shared with the catalog grid
  // tiles when the "Display Heat Map Gradient on Grid" option is on),
  // so the planogram and the per-tile gradient bars stay in lockstep.
  const productColors = useMemo(() => {
    const colorMap = new Map<string, string | undefined>();
    valuesByProductId.forEach((value, productId) => {
      if (value === undefined || !freshByProductId.get(productId)) {
        colorMap.set(productId, undefined);
        return;
      }
      const band = scales.find(
        (s) => value <= s.upperBound && value >= s.lowerBound,
      );
      colorMap.set(productId, band?.color ?? scales[scales.length - 1]?.color);
    });
    return colorMap;
  }, [valuesByProductId, freshByProductId, scales]);

  return (
    <StyledPlanogramPane>
      <StyledPlanogramBody>
        <StyledPlanogramGridWrapper>
          <Typography variant="modalSubtitle">
            {intl.formatMessage({
              id: "heatMap.label",
              defaultMessage: "Heat Map",
            })}
          </Typography>

          <StyledPageColumns>
            {batchPages.pages.map((page, idx) => (
              <StyledPageColumn key={page.pageNumber}>
                <StyledPageColumnLabel>
                  {`P#${idx + 1}`}
                </StyledPageColumnLabel>
                <StyledPageGrid
                  columns={columns}
                  sideLength={sideLength}
                  cellMargin={CELL_MARGIN}
                >
                  {page.productIds.map((productId) => {
                    const isFresh = freshByProductId.get(productId);
                    // Date-reliant cells skeleton when their chunk hasn't
                    // landed for the current range yet (and we're still
                    // fetching). Once not fetching, non-fresh cells render
                    // uncolored instead of staying as skeletons.
                    const showSkeleton =
                      isDateReliant && !isFresh && isFetching;
                    if (showSkeleton) {
                      return (
                        <Skeleton
                          key={productId}
                          variant="rectangular"
                          animation="wave"
                          width={sideLength}
                          height={sideLength}
                          sx={{ borderRadius: `${borderRadius}px` }}
                          data-testid={`heatmap-skeleton-${productId}`}
                        />
                      );
                    }

                    const product = productsById[productId];
                    const productName = product?.name ?? productId;
                    const productSku = product?.code ?? "-";
                    const metricValue = valuesByProductId.get(productId);
                    const tooltipTitle = (
                      <Box sx={styles.cellTooltip}>
                        <Typography
                          variant="body2"
                          sx={styles.cellTooltipTitle}
                        >
                          {productName}
                        </Typography>
                        <Typography variant="caption">
                          {intl.formatMessage(
                            {
                              id: "heatMap.tooltip.id",
                              defaultMessage: "ID: {id}",
                            },
                            { id: productSku },
                          )}
                        </Typography>
                        <Typography variant="caption">
                          {intl.formatMessage(
                            {
                              id: "heatMap.tooltip.metric",
                              defaultMessage: "{metric}: {value}",
                            },
                            {
                              metric,
                              value:
                                metricValue !== undefined
                                  ? formatHeatMapMetricValue(metricValue)
                                  : "-",
                            },
                          )}
                        </Typography>
                      </Box>
                    );

                    const handleCellClick = () => {
                      const sequenceItem = product
                        ? ({
                            productId: product.productId,
                          } as ProductSequenceData)
                        : ({ productId } as ProductSequenceData);
                      dispatch(setSelectedItems([sequenceItem]));
                      dispatch(setPendingScrollTarget(productId));
                      dispatch(setModalState(HEAT_MAP_MODAL_ID, false));
                    };

                    return (
                      <Tooltip
                        key={productId}
                        title={tooltipTitle}
                        placement="top"
                        arrow
                        slotProps={{
                          tooltip: { sx: styles.cellTooltipPopper },
                        }}
                      >
                        <StyledPlanogramCell
                          cellColor={productColors.get(productId)}
                          sideLength={sideLength}
                          borderRadius={borderRadius}
                          data-product-id={productId}
                          data-testid={`heatmap-cell-${productId}`}
                          onClick={handleCellClick}
                        />
                      </Tooltip>
                    );
                  })}
                </StyledPageGrid>
              </StyledPageColumn>
            ))}
          </StyledPageColumns>
        </StyledPlanogramGridWrapper>

        {scales.length > 0 && (
          <Box sx={styles.planogramBox}>
            <Typography variant="heading">{metric}</Typography>
            <StyledColorScale>
              {scales.map((s, idx) => (
                <StyledColorScaleBand key={idx} bandColor={s.color}>
                  <StyledColorScaleValue variant="scaleNumbers">
                    {formatHeatMapMetricValue(s.upperBound)}
                  </StyledColorScaleValue>
                </StyledColorScaleBand>
              ))}
              <Box sx={styles.colorScaleBox}>
                <StyledColorScaleValue variant="scaleNumbers">
                  {formatHeatMapMetricValue(
                    scales[scales.length - 1].lowerBound,
                  )}
                </StyledColorScaleValue>
              </Box>
            </StyledColorScale>
          </Box>
        )}
      </StyledPlanogramBody>

      {batchPages.totalProductCount === 0 && (
        <Box sx={styles.noProductsBox}>
          {intl.formatMessage({
            id: "heatMap.noProducts",
            defaultMessage: "No products to display.",
          })}
        </Box>
      )}
    </StyledPlanogramPane>
  );
};

export default HeatMapPlanogram;
